import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import { apiRequest } from '../../utils';
import './MemberHome.css';

/* The screen behind the "Assessments" tile: the two anonymous links the admin sends out (one for
   leaders, one for everyone), how many have answered each, and the company-level feedback those
   answers produce.

   The link is copied together with a warm sentence, which is why the button says "Send test" and
   not "Copy link" — an admin is about to paste this into WhatsApp, not into a spreadsheet. */

const CompanyAssessments = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    apiRequest('GET', '/bravos/assessments')
      .then(setData)
      .catch(() => navigate('/member-home'));
  }, [navigate]);

  const copyLink = (key, link, message) => {
    const text = `${message}\n${link}`;
    const done = () => { setCopied(key); setTimeout(() => setCopied(null), 2000); };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(done);
    } else {
      done();
    }
  };

  const feedback = data && data.company_feedback;
  const hasFeedback = feedback && !feedback.below_threshold
    && ((feedback.strengths || []).length > 0 || (feedback.improvements || []).length > 0);

  return (
    <Base activeNav="home">
      <Card>
        <Back to="/member-home" />
        <Title my="1" className="mt-3">
          <Icon name="users" theme="primary" className="me-2" />
          {t('bravosAssessments')}
        </Title>

        {data === null ? (
          <span className="auth__spinner" />
        ) : (
          <>
            <p className="bravos-q__hint mb-2">{t('inviteYourTeamNote')}</p>
            {(data.distribution || []).map((d) => (
              <div key={d.key} className="bravos-dist">
                <div className="bravos-dist__body">
                  <span className="bravos-dist__name">{d.name}</span>
                  <span className="bravos-dist__count">
                    {t('responsesCount', { n: d.count })}
                  </span>
                </div>
                <button
                  type="button"
                  className="bravos-copy"
                  title={t('sendTestTeamHint')}
                  onClick={() => copyLink(d.key, d.link, t('bravosDistShareText'))}
                >
                  <Icon name={copied === d.key ? 'check' : 'paper-plane'} />
                  {copied === d.key ? t('copied') : t('sendTest')}
                </button>
              </div>
            ))}
          </>
        )}
      </Card>

      {feedback && (
        <Card>
          <Title my="1" className="mt-2">
            <Icon name="star" theme="primary" className="me-2" />
            {t('companyFeedback')}
          </Title>
          {feedback.below_threshold ? (
            <p className="bravos-q__hint mb-2">{t('companyFeedbackNeedData')}</p>
          ) : hasFeedback ? (
            <>
              <p className="bravos-q__hint mb-2">{t('companyFeedbackNote')}</p>
              {(feedback.strengths || []).length > 0 && (
                <>
                  <p className="bravos-cal__label">{t('whatsWorking')}</p>
                  {feedback.strengths.map((s, i) => (
                    <div key={`s${i}`} className="bravos-fb-block">
                      {s.subdimension && <p className="bravos-fb-sub">{s.subdimension}</p>}
                      <p className="bravos-fb-block__text">{s.feedback}</p>
                    </div>
                  ))}
                </>
              )}
              {(feedback.improvements || []).length > 0 && (
                <>
                  <p className="bravos-cal__label bravos-cal__label--past">{t('whereToFocus')}</p>
                  {feedback.improvements.map((s, i) => (
                    <div key={`i${i}`} className="bravos-fb-block">
                      {s.subdimension && <p className="bravos-fb-sub">{s.subdimension}</p>}
                      <p className="bravos-fb-block__text">{s.feedback}</p>
                    </div>
                  ))}
                </>
              )}
            </>
          ) : (
            <p className="bravos-q__hint mb-0">{t('companyFeedbackNeedData')}</p>
          )}
        </Card>
      )}
    </Base>
  );
};

export default CompanyAssessments;
